// src/pages/hotel-bookings/AdminHotelBookingDetailsPage.tsx
//
// One hotel booking for operations: stay, guests, what the customer paid, what
// the supplier bills us (and our margin), and the cancellation state — with
// the admin cancel action.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import { useAdminBookingById } from "@/api/features/bookings/queries";
import {
  useAdminCancelHotelBooking,
  useAdminHotelCancellation,
  useAdminHotelVoucher,
  type AdminHotelCancellationOverview,
  type AdminHotelVoucher,
} from "@/api/features/hotel-bookings";

import CancelHotelBookingDialog from "./components/CancelHotelBookingDialog";
import { BookingStatusPill, Card, CancellationStatusPill, Row } from "./components/hotelBookingUi";
import { formatDate, formatDateTime, formatMoney } from "./utils/hotelBookingFormat";

const LIST_PATH = "/hotel-bookings";

export default function AdminHotelBookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const bookingId = id && /^\d+$/.test(id) ? id : null;

  const voucherQ = useAdminHotelVoucher(bookingId);
  const cancellationQ = useAdminHotelCancellation(bookingId);
  // Customer name/email live on the booking aggregate, not the voucher.
  const bookingQ = useAdminBookingById(bookingId);

  const voucher = voucherQ.data ?? null;

  if (!bookingId || voucherQ.isError) {
    return (
      <AdminLayout>
        <BackLink />
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {voucherQ.error?.message || "Hotel booking not found."}
        </div>
      </AdminLayout>
    );
  }

  if (voucherQ.isLoading || !voucher) {
    return (
      <AdminLayout>
        <BackLink />
        <div className="mt-4 rounded-2xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          Loading hotel booking…
        </div>
      </AdminLayout>
    );
  }

  const booking = bookingQ.data?.booking ?? null;
  const customer = {
    name:
      booking?.customerName ||
      booking?.customer?.displayName ||
      (voucher.customerUserId != null ? `User #${voucher.customerUserId}` : null),
    email: booking?.customerEmail || booking?.customer?.email || null,
    phone: booking?.customerPhone || booking?.customer?.phone || null,
  };

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <BackLink />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{voucher.bookingCode}</h1>
            <BookingStatusPill status={voucher.bookingStatus} />
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {voucher.hotel.name || "Hotel"}
            {voucher.hotel.cityName ? ` · ${voucher.hotel.cityName}` : ""}
          </p>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <CancelAction bookingId={bookingId} overview={cancellationQ.data ?? null} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <StayCard voucher={voucher} />
          <GuestsCard voucher={voucher} />
          <PolicyCard voucher={voucher} />
        </div>

        <div className="space-y-4">
          <PriceCard voucher={voucher} />
          <SupplierCard voucher={voucher} />
          <CancellationCard
            query={cancellationQ}
            currency={voucher.payment.currency}
          />
          <Card title="Customer">
            <dl>
              <Row label="Name" value={customer.name} />
              <Row label="Email" value={customer.email} />
              <Row label="Phone" value={customer.phone} />
            </dl>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

function BackLink() {
  return (
    <Link to={LIST_PATH} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
      <FiArrowLeft aria-hidden /> Hotel Bookings
    </Link>
  );
}

function CancelAction({
  bookingId,
  overview,
}: {
  bookingId: string;
  overview: AdminHotelCancellationOverview | null;
}) {
  const [open, setOpen] = useState(false);
  const cancel = useAdminCancelHotelBooking(bookingId);

  if (!overview) return null;

  if (!overview.cancellable) {
    // Say why, rather than leaving an unexplained missing button.
    return overview.blockedReason ? (
      <p className="max-w-xs text-right text-xs text-gray-500">{overview.blockedReason.message}</p>
    ) : null;
  }

  return (
    <>
      <button
        onClick={() => {
          cancel.reset();
          setOpen(true);
        }}
        className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
      >
        Cancel booking
      </button>

      <CancelHotelBookingDialog
        open={open}
        overview={overview}
        isPending={cancel.isPending}
        errorMessage={cancel.error?.message ?? null}
        onCancel={() => setOpen(false)}
        onConfirm={(reason) =>
          cancel.mutate({ reason }, { onSuccess: () => setOpen(false) })
        }
      />
    </>
  );
}

function StayCard({ voucher }: { voucher: AdminHotelVoucher }) {
  const { hotel, stay } = voucher;
  return (
    <Card title="Stay">
      <div className="grid gap-x-8 sm:grid-cols-2">
        <dl>
          <Row label="Hotel" value={hotel.name} strong />
          <Row label="Address" value={hotel.address} />
          <Row
            label="Location"
            value={[hotel.cityName, hotel.countryName].filter(Boolean).join(", ")}
          />
          <Row label="Star rating" value={hotel.starRating ? `${hotel.starRating}★` : null} />
        </dl>
        <dl>
          <Row
            label="Check-in"
            value={`${formatDate(stay.checkIn)}${hotel.checkInTime ? ` · ${hotel.checkInTime}` : ""}`}
          />
          <Row
            label="Check-out"
            value={`${formatDate(stay.checkOut)}${hotel.checkOutTime ? ` · ${hotel.checkOutTime}` : ""}`}
          />
          <Row label="Nights" value={stay.nights} />
          <Row label="Rooms" value={stay.noOfRooms} />
          <Row label="Meal plan" value={voucher.mealType} />
          <Row label="Booked" value={formatDateTime(voucher.bookedAt)} />
        </dl>
      </div>
      {voucher.inclusions.length ? (
        <p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600">
          <span className="font-medium text-gray-700">Inclusions:</span> {voucher.inclusions.join(" · ")}
        </p>
      ) : null}
    </Card>
  );
}

function GuestsCard({ voucher }: { voucher: AdminHotelVoucher }) {
  const rooms = voucher.rooms.length
    ? voucher.rooms
    : [{ roomIndex: 0, roomType: voucher.stay.roomName ?? "Room", inclusion: null, adults: 0, children: 0, guestNames: [] }];

  return (
    <Card title="Rooms & guests">
      <div className="divide-y divide-gray-100">
        {rooms.map((room) => {
          const guests = voucher.guests.filter((g) => g.roomIndex === room.roomIndex);
          return (
            <div key={room.roomIndex} className="py-2 first:pt-0 last:pb-0">
              <p className="text-sm font-medium text-gray-900">
                Room {room.roomIndex + 1}: {room.roomType}
              </p>
              {guests.length ? (
                <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
                  {guests.map((g, i) => (
                    <li key={i}>
                      {g.name || "—"}
                      <span className="text-xs text-gray-500">
                        {" "}
                        · {g.type}
                        {g.age != null ? ` (${g.age})` : ""}
                        {g.isLead ? " · Lead guest" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-gray-500">No guest names recorded.</p>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PolicyCard({ voucher }: { voucher: AdminHotelVoucher }) {
  const policy = voucher.cancellation;
  const currency = voucher.payment.currency;
  return (
    <Card
      title="Cancellation policy (as booked)"
      right={
        policy ? (
          <span className={policy.isRefundable ? "text-xs text-emerald-700" : "text-xs text-red-600"}>
            {policy.isRefundable ? "Refundable" : "Non-refundable"}
          </span>
        ) : null
      }
    >
      {policy?.windows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500">
              <tr>
                <th className="py-1 text-left font-medium">From</th>
                <th className="py-1 text-left font-medium">To</th>
                <th className="py-1 text-right font-medium">Charge</th>
              </tr>
            </thead>
            <tbody>
              {policy.windows.map((w, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1.5 text-gray-700">{w.from ? formatDateTime(w.from) : "Now"}</td>
                  <td className="py-1.5 text-gray-700">{w.to ? formatDateTime(w.to) : "Check-in"}</td>
                  <td className="py-1.5 text-right text-gray-900">{formatMoney(w.amount, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No cancellation windows were recorded for this booking.</p>
      )}
    </Card>
  );
}

function PriceCard({ voucher }: { voucher: AdminHotelVoucher }) {
  const { payment } = voucher;
  const currency = payment.currency;
  const b = payment.breakdown;

  return (
    <Card title="Customer price">
      <dl>
        {b ? (
          <>
            <Row label="Room rate" value={formatMoney(b.roomRate, currency)} />
            <Row label="Hotel taxes" value={formatMoney(b.hotelTaxes, currency)} />
            <Row label="Convenience fee" value={formatMoney(b.convenienceFee, currency)} />
            <Row label="GST" value={formatMoney(b.gst, currency)} />
          </>
        ) : (
          <Row label="Total" value={formatMoney(payment.grossAmount ?? payment.amount, currency)} />
        )}
        {payment.discount > 0 ? (
          <Row label="Promo discount" value={`− ${formatMoney(payment.discount, currency)}`} />
        ) : null}
        <div className="mt-1 border-t border-gray-100 pt-1">
          <Row label="Paid" value={formatMoney(payment.amount, currency)} strong />
        </div>
      </dl>
      {!b ? (
        <p className="mt-1 text-xs text-gray-500">Booked before the price breakdown was recorded.</p>
      ) : null}
    </Card>
  );
}

function SupplierCard({ voucher }: { voucher: AdminHotelVoucher }) {
  const { admin } = voucher;
  const currency = admin.supplierCurrency ?? voucher.payment.currency;
  const margin =
    admin.markup != null && admin.markupTax != null ? admin.markup + admin.markupTax : null;
  const rateLabel =
    admin.markupRate != null
      ? admin.markupMethod === "FLAT"
        ? `${formatMoney(admin.markupRate, currency)} flat`
        : `${admin.markupRate}%`
      : null;

  return (
    <Card title="Supplier & margin">
      <dl>
        <Row label="Supplier" value={admin.supplier} strong />
        <Row label="Supplier status" value={admin.supplierStatus} />
        <Row label="Supplier booking ID" value={admin.supplierBookingId ?? voucher.bookingRefNo} />
        <Row label="Confirmation no." value={voucher.confirmationNo} />
        <Row label="Invoice no." value={voucher.invoiceNumber} />
        <Row label="Vouchered" value={admin.isVouchered ? "Yes" : "No"} />
        <div className="mt-1 border-t border-gray-100 pt-1">
          <Row label="Supplier cost" value={formatMoney(admin.supplierAmount, currency)} />
          <Row
            label="Markup"
            value={
              admin.markup != null
                ? `${formatMoney(admin.markup, currency)}${rateLabel ? ` (${rateLabel})` : ""}`
                : null
            }
          />
          <Row label="GST on markup" value={formatMoney(admin.markupTax, currency)} />
          <Row label="Margin (incl. GST)" value={formatMoney(margin, currency)} strong />
          <Row label="Kept on cancellation" value={formatMoney(admin.nonRefundableMargin, currency)} />
          <Row label="Customer type" value={admin.customerType} />
        </div>
      </dl>
    </Card>
  );
}

function CancellationCard({
  query,
  currency,
}: {
  query: ReturnType<typeof useAdminHotelCancellation>;
  currency: string | null;
}) {
  const overview = query.data ?? null;
  const record = overview?.cancellation ?? null;
  const detail = overview?.admin ?? null;

  return (
    <Card
      title="Cancellation"
      right={
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
          aria-label="Refresh cancellation status"
        >
          <FiRefreshCw className={query.isFetching ? "animate-spin" : ""} aria-hidden /> Refresh
        </button>
      }
    >
      {query.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : query.isError ? (
        <p className="text-sm text-red-700">{query.error?.message || "Could not load cancellation."}</p>
      ) : !record ? (
        <p className="text-sm text-gray-500">No cancellation has been requested.</p>
      ) : (
        <dl>
          <Row label="Status" value={<CancellationStatusPill status={record.status} />} />
          <Row label="Refund" value={record.refundStatus.replace(/_/g, " ")} />
          <Row label="Requested via" value={record.requestedVia} />
          <Row label="Requested by" value={detail?.requestedBy} />
          <Row label="Requested" value={formatDateTime(record.requestedAt)} />
          <Row label="Reason" value={record.reason} />
          <Row label="Cancellation charge" value={formatMoney(record.cancellationCharge, currency)} />
          <Row label="Refund amount" value={formatMoney(record.refundAmount, currency)} strong />
          <Row label="Cancelled" value={formatDateTime(record.cancelledAt)} />
          <Row label="Refunded" value={formatDateTime(record.refundedAt)} />
          {detail ? (
            <div className="mt-1 border-t border-gray-100 pt-1">
              <Row label="Change request ID" value={detail.changeRequestId} />
              <Row label="Supplier acknowledged" value={detail.supplierAcknowledged ? "Yes" : "No"} />
              <Row
                label="Supplier refund"
                value={detail.supplierRefundAmount != null ? formatMoney(detail.supplierRefundAmount, currency) : null}
              />
              <Row label="Supplier message" value={detail.supplierMessage} />
              <Row label="Review reason" value={detail.reviewReason} />
              <Row
                label="Status checks"
                value={`${detail.pollAttempts}${detail.lastPolledAt ? ` · last ${formatDateTime(detail.lastPolledAt)}` : ""}`}
              />
            </div>
          ) : null}
        </dl>
      )}
    </Card>
  );
}
